import { useUserSession } from '@solinkcorp/authentication';
import React from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { SDM_SWR_REFRESH_INTERVAL_MS } from '../constants';
import { useSDMClient } from '../contexts/CallhomeClientContext';
import { type ServiceDisplayLayout, type ServiceDisplayWidget } from '../layout/types';

const SERVICE_DISPLAY_LAYOUTS_KEY = '/api/ServiceDisplayLayouts';

/**
 * Returns threshold IDs that exist in the cached (server) layout but are not in the current
 * widgets payload (user unchecked them). Used to call bulk-delete before PUT widgets.
 */
export function getThresholdIdsToDelete(
  cachedLayout: ServiceDisplayLayout | undefined,
  widgetsToSave: ServiceDisplayWidget[],
): string[] {
  if (!cachedLayout?.widgets?.length) return [];
  const idsToDelete: string[] = [];
  for (const widget of widgetsToSave) {
    const cachedWidget = cachedLayout.widgets.find((w) => w.id === widget.id);
    const originalIds = (cachedWidget?.thresholds ?? [])
      .map((t) => t.id)
      .filter((id): id is string => Boolean(id));
    const currentIds = new Set(
      (widget.thresholds ?? []).map((t) => t.id).filter((id): id is string => Boolean(id)),
    );
    for (const id of originalIds) {
      if (!currentIds.has(id)) idsToDelete.push(id);
    }
  }
  return idsToDelete;
}

function useInvalidateServiceDisplayLayoutsCache() {
  const { mutate } = useSWRConfig();

  return React.useCallback(async () => {
    await mutate(SERVICE_DISPLAY_LAYOUTS_KEY);
  }, [mutate]);
}

export function useServiceDisplayLayouts() {
  const callHomeClient = useSDMClient();

  const serviceDisplayLayouts = useSWR(
    SERVICE_DISPLAY_LAYOUTS_KEY,
    () => callHomeClient.getServiceDisplayLayouts<ServiceDisplayLayout>(),
    {
      suspense: true,
      refreshInterval: SDM_SWR_REFRESH_INTERVAL_MS,
      refreshWhenHidden: true,
    },
  );

  return serviceDisplayLayouts;
}

export function useCustomerDefaultSDMLayout() {
  const callHomeClient = useSDMClient();
  const { customerId } = useUserSession();
  const { data, mutate } = useSWR(
    `${SERVICE_DISPLAY_LAYOUTS_KEY}/defaultSDM`,
    () => callHomeClient.getCustomerDefaultSdmLayout(customerId),
    { suspense: true },
  );
  return { data: data?.defaultSdmLayoutId, customerId, mutate };
}

export function useUpdateServiceDisplayWidgets() {
  const { customerId } = useUserSession();
  const invalidateServiceDisplayLayoutsCache = useInvalidateServiceDisplayLayoutsCache();
  const { data } = useServiceDisplayLayouts();
  const callHomeClient = useSDMClient();

  return React.useCallback(
    async (widgets: ServiceDisplayWidget[], layoutId: string | undefined, layoutName: string) => {
      const layout = data?.find((layout: ServiceDisplayLayout) => layout.id === layoutId);
      const serverKnownIds = new Set(
        layout?.widgets?.map((w: ServiceDisplayWidget) => w.id).filter(Boolean) ?? [],
      );

      const widgetsForApi: ServiceDisplayWidget[] = widgets.map((w: ServiceDisplayWidget) =>
        serverKnownIds.has(w.id ?? '')
          ? { ...w }
          : ({ ...w, id: undefined } as ServiceDisplayWidget),
      );

      let targetLayoutId: string;

      if (!layout) {
        const newLayout = await callHomeClient.createServiceDisplayLayout({
          customerId,
          name: layoutName,
        });
        targetLayoutId = newLayout.id;
      } else {
        targetLayoutId = layout.id;
        if (layoutName !== layout.name) {
          await callHomeClient.updateServiceDisplayLayout(layout.id, { name: layoutName });
        }
      }

      const thresholdIdsToDelete = getThresholdIdsToDelete(layout, widgets);
      if (thresholdIdsToDelete.length > 0) {
        await callHomeClient.bulkDeleteServiceDisplayDayPartThresholds(thresholdIdsToDelete);
      }

      await callHomeClient.updateServiceDisplayWidgets(targetLayoutId, {
        widgets: widgetsForApi,
      });

      await invalidateServiceDisplayLayoutsCache();
    },
    [data, invalidateServiceDisplayLayoutsCache, customerId, callHomeClient],
  );
}

export function useDeleteServiceDisplayWidgets() {
  const callHomeClient = useSDMClient();

  return React.useCallback(
    async (layoutId: string, widgetIds: string[]) => {
      if (widgetIds.length === 0) return;
      await callHomeClient.deleteServiceDisplayWidgets(layoutId, widgetIds);
    },
    [callHomeClient],
  );
}

export function useDeleteServiceDisplayLayout() {
  const invalidateServiceDisplayLayoutsCache = useInvalidateServiceDisplayLayoutsCache();
  const callHomeClient = useSDMClient();

  return React.useCallback(
    async (layoutId: string) => {
      await callHomeClient.deleteServiceDisplayLayout(layoutId);
      await invalidateServiceDisplayLayoutsCache();
    },
    [invalidateServiceDisplayLayoutsCache, callHomeClient],
  );
}
